import { AnswerFilePreview } from "@/components/license-details/cards/answer-file-preview";
import { LiveLinkAnswer } from "@/components/license-details/cards/live-link-answer";
import { PhotoAnswerReview } from "@/components/license-details/cards/photo-answer-review";
import type { PhotoAnswerReviewProperty } from "@/components/license-details/cards/photo-answer-review";
import { UrlAnswerList } from "@/components/url-answer-list";
import type { QuestionResponseSchema } from "@/hera-api/client/types.gen";
import {
  extractAnswerFileToken,
  isAnswerFileAnswerType,
} from "@/lib/answer-file";
import { isNacStyleAnswerType } from "@/lib/nac-answer-types";
import { normalizePhotoAnswers } from "@/lib/photo-answer";
import {
  formatAnswerValue,
  formatConfirmationAnswer,
  formatPhoneNumber,
} from "@/lib/utils";

function hasAnswer(answer: unknown): boolean {
  if (answer === undefined || answer === null) return false;
  if (typeof answer === "string" && answer.trim() === "") return false;
  if (Array.isArray(answer) && answer.length === 0) return false;
  if (typeof answer === "boolean" || typeof answer === "number") return true;
  if (extractAnswerFileToken(answer)) return true;
  if (typeof answer === "object") {
    return Object.keys(answer as object).length > 0;
  }
  return true;
}

export function InspectionAnswersPanel({
  questions,
  answers,
  elevatedDialog = false,
  emptyMessage = "No answers recorded.",
  className,
  property = null,
}: {
  questions: QuestionResponseSchema[];
  answers: Record<string, unknown> | null | undefined;
  elevatedDialog?: boolean;
  emptyMessage?: string;
  className?: string;
  /** Property location/address for photo maps and captions. */
  property?: PhotoAnswerReviewProperty | null;
}) {
  const answerMap = answers ?? {};
  const inspectionQuestions = questions
    .filter(
      (q) =>
        q.question_type === "inspection" &&
        q.answer_type !== "information" &&
        !isNacStyleAnswerType(q.answer_type),
    )
    .slice()
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

  const answered = inspectionQuestions.filter((q) =>
    hasAnswer(answerMap[q.id]),
  );

  // Also surface answer keys that lack a question definition (deleted questions).
  const knownIds = new Set(inspectionQuestions.map((q) => q.id));
  const orphanEntries = Object.entries(answerMap).filter(
    ([id, value]) => !knownIds.has(id) && hasAnswer(value),
  );

  if (answered.length === 0 && orphanEntries.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyMessage}</p>;
  }

  return (
    <div
      className={className ?? "space-y-3"}
      data-testid="inspection-answers-panel"
    >
      {answered.map((question) => {
        const answer = answerMap[question.id];
        return (
          <div key={question.id}>
            <p className="text-sm font-bold">{question.question}</p>
            <InspectionAnswerValue
              answer={answer}
              answerType={question.answer_type}
              urlTemplate={question.url_template}
              copyValueToClipboard={question.copy_value_to_clipboard ?? false}
              httpMethod={question.http_method}
              valueExtractRegex={question.value_extract_regex}
              requestFields={question.request_fields}
              browserHelperConfig={question.browser_helper_config}
              elevatedDialog={elevatedDialog}
              property={property}
            />
          </div>
        );
      })}
      {orphanEntries.map(([questionId, answer]) => (
        <div key={questionId}>
          <p className="text-sm font-bold text-muted-foreground">
            Question {questionId.slice(0, 8)}…
          </p>
          <InspectionAnswerValue
            answer={answer}
            elevatedDialog={elevatedDialog}
            property={property}
          />
        </div>
      ))}
    </div>
  );
}

function InspectionAnswerValue({
  answer,
  answerType,
  urlTemplate,
  copyValueToClipboard = false,
  httpMethod,
  valueExtractRegex,
  requestFields,
  browserHelperConfig,
  elevatedDialog = false,
  property = null,
}: {
  answer: unknown;
  answerType?: string;
  urlTemplate?: string | null;
  copyValueToClipboard?: boolean;
  httpMethod?: string | null;
  valueExtractRegex?: string | null;
  requestFields?: Record<string, string> | null;
  browserHelperConfig?: Record<string, unknown> | null;
  elevatedDialog?: boolean;
  property?: PhotoAnswerReviewProperty | null;
}) {
  if (answerType === "url") {
    return <UrlAnswerList value={answer} />;
  }

  const photoAnswers =
    answerType === "photo" || Array.isArray(answer)
      ? normalizePhotoAnswers(answer)
      : [];

  if (photoAnswers.length > 0) {
    return (
      <PhotoAnswerReview
        photos={photoAnswers}
        property={property}
        elevatedDialog={elevatedDialog}
      />
    );
  }

  const looksLikeFile =
    isAnswerFileAnswerType(answerType) ||
    (typeof answer === "object" &&
      answer !== null &&
      !!extractAnswerFileToken(answer));

  if (looksLikeFile && extractAnswerFileToken(answer)) {
    return (
      <AnswerFilePreview
        answer={answer}
        answerType={answerType}
        elevatedDialog={elevatedDialog}
        className="mt-1"
      />
    );
  }

  if (
    answerType === "live_link" &&
    typeof answer === "string" &&
    answer.trim()
  ) {
    return (
      <LiveLinkAnswer
        value={answer}
        urlTemplate={urlTemplate}
        copyValueToClipboard={copyValueToClipboard}
        httpMethod={httpMethod}
        valueExtractRegex={valueExtractRegex}
        requestFields={requestFields}
        browserHelperConfig={browserHelperConfig}
      />
    );
  }

  if (answerType === "confirmation") {
    return (
      <p className="text-sm whitespace-pre-wrap">
        {formatConfirmationAnswer(answer)}
      </p>
    );
  }

  const formatted =
    answerType === "phone_number" && typeof answer === "string"
      ? formatPhoneNumber(answer)
      : formatAnswerValue(answer);

  return <p className="text-sm whitespace-pre-wrap">{formatted}</p>;
}
